import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import { DataOptions } from "vuetify";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { Airline, AirlineResponse } from "@/models/api/administration/airlines/Airline";
import {
  getAirlineById,
  getAirlineTable,
  addAirline,
  editAirline,
  removeAirline,
  changeAirlineContractor,
  removeAirlineContractor,
} from "@/requests/administration/administrationAirlines";
import _ from "lodash";
import { FILTER_NAMES } from "@/static/filterNames";
import { AirlineEditAdd } from "@/models/api/administration/airlines/AirlineEditAdd";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { Contractor } from "@/models/api/Contractor";

export interface AdministrationAirlinesStateInterface {
  filters: null | Array<CriterionBase>;
  airlines: Array<Airline>;
  countItem: number | null;
  dataTableOptions: DataOptions;
  selectedAirline: Airline | null;
  isEditDialog: boolean;
  airlineEdit: AirlineEditAdd;
}

export const AdministrationAirlinesState: AdministrationAirlinesStateInterface = {
  filters: null,
  airlines: [],
  countItem: null,
  dataTableOptions: new DefaultDataOptions(),
  selectedAirline: null,
  isEditDialog: false,
  airlineEdit: new AirlineEditAdd(),
};

const AdministrationAirlinesModule = defineModule({
  state: AdministrationAirlinesState as AdministrationAirlinesStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setFilters(state, param: Array<CriterionBase>) {
      state.filters = param;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = param;
    },
    setAirlines(state, param: AirlineResponse) {
      state.airlines = param.items;
      state.countItem = param.totalCount;
    },
    setSelectedAirline(state, param: Airline | null) {
      state.selectedAirline = param;
    },
    setIsEditDialog(state, param: boolean) {
      state.isEditDialog = param;
    },
    setAirlineEdit(state, param: AirlineEditAdd) {
      state.airlineEdit = param;
    },
    clearAirlineEdit(state) {
      state.airlineEdit.icaoCode = "";
      state.airlineEdit.iataCode = "";
      state.airlineEdit.name = "";
      state.airlineEdit.numericCode = "";
      state.airlineEdit.isLowCost = false;
    },
  },
  actions: {
    async downloadAirlines(context) {
      const { commit, state } = _moduleActionContext(context);
      const airlines = await getAirlineTable({
        count: state.dataTableOptions.itemsPerPage,
        page: state.dataTableOptions.page - 1,
        criterions: _.cloneDeep(state.filters),
        filterName: FILTER_NAMES.AirlineStandardFilter,
      });
      commit.setAirlines(airlines);
    },
    async showAirlineDialog(context, airline: Airline | null) {
      const { commit } = _moduleActionContext(context);
      commit.setSelectedAirline(airline);
      if (airline) commit.setAirlineEdit(await getAirlineById(airline.id));
      else commit.clearAirlineEdit();
      commit.setIsEditDialog(true);
    },
    async removeAirline(context, airlineId: number) {
      const { dispatch, rootDispatch, commit } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (
        await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("doYouReallyWantToRemoveAirline")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("no")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("yes")),
                color: "warning",
                value: true,
              },
            ],
            "450px"
          )
        )
      ) {
        await removeAirline(airlineId);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
      await dispatch.downloadAirlines();
      commit.setSelectedAirline(null);
    },
    async addAirline(context, airline: AirlineEditAdd) {
      const { dispatch, rootDispatch, commit } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      await addAirline(airline);
      rootDispatch.popup.addSavedOkSnackbar();
      await dispatch.downloadAirlines();
      commit.setIsEditDialog(false);
      commit.setSelectedAirline(null);
    },
    async editAirline(context, airline: AirlineEditAdd) {
      const { dispatch, rootDispatch, commit, state } = _moduleActionContext(context);
      if (!state.selectedAirline) return;
      await rootDispatch.failIfRequestsInProgress();
      await editAirline(state.selectedAirline.id, airline);
      await dispatch.downloadAirlines();
      rootDispatch.popup.addSavedOkSnackbar();
      commit.setIsEditDialog(false);
    },
    async changeAirlineContractor(context, contractor: Contractor) {
      const { rootDispatch, dispatch, commit, state } = _moduleActionContext(context);
      if (state.selectedAirline !== null && contractor.id) {
        console.log("changeAirlineContractor", state.selectedAirline, contractor.id);
        await rootDispatch.failIfRequestsInProgress();
        await changeAirlineContractor(state.selectedAirline.id, contractor.id);
        commit.setSelectedAirline(null);
        await dispatch.downloadAirlines();
        rootDispatch.popup.addSavedOkSnackbar();
      }
    },
    async removeAirlineContractor(context, id: number) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      await removeAirlineContractor(id);
      await dispatch.downloadAirlines();
      rootDispatch.popup.addSavedOkSnackbar();
    },
  },
});
export default AdministrationAirlinesModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationAirlinesModule);
